import { format, utcToZonedTime } from 'date-fns-tz';

import { OrdemDaFila, ordenarFila } from 'pages/RealizacaoDestaque/ordenar-fila';

import { AcaoEcoa, eixoDaAcao } from './acoes-do-semestre';

/**
 * Regras da lista da fila do tutor e do coordenador (ECN-49 a ECN-53). Rodam no navegador sobre a mesma lista que a
 * `ApreciarAcao` já monta (`filteredData`), sem regra nova: os contadores repetem os de hoje da página.
 */

export { ordenarFila };
export type { OrdemDaFila };

/** Critério de cada indicador; o mesmo critério filtra a lista quando o indicador é acionado (ECN-50). */
export type CriterioDaFila = 'aguardando' | 'cota' | 'ciencia' | 'sem-feedback';

export interface ContextoDaFila {
  /** `user._id` do usuário logado: a página conta o feedback por ele. */
  chaveDoUsuario: string | undefined;
  /** Opção "semestral" da fila (bonificação). */
  semestral: boolean;
}

export interface IndicadorDaFilaEcoa {
  criterio: CriterioDaFila;
  rotulo: string;
  valor: string;
}

/** Feedback do usuário na ação, pela regra de `feedbackTasks` da `ApreciarAcao` (inclusive a lateralidade). */
export function temFeedbackDoUsuario(acao: AcaoEcoa, chaveDoUsuario: string | undefined): boolean {
  return (
    Array.isArray(acao.feedback) &&
    acao.feedback.some(
      feedback =>
        (feedback.evaluatorId === chaveDoUsuario && !feedback.original_task_manager?.playerId) ||
        (!!feedback.original_task_manager?.playerId && feedback.original_task_manager.playerId === chaveDoUsuario),
    )
  );
}

/** Ação ainda sem validação do tutor ou do coordenador. */
export const aguardandoValidacao = (acao: AcaoEcoa) => acao.status === 'aguardando';
/** `validTasks` da página: tudo que já passou da validação. */
export const acaoValidada = (acao: AcaoEcoa) =>
  acao.status !== 'rejeitado' && acao.status !== 'aguardando' && acao.status !== 'ciente';
/** `knownTasks` da página ("Total de ações cientes" de antes): ciência ou validação (`multiplicador >= 1`). */
export const acaoComCiencia = (acao: AcaoEcoa) => acao.status === 'ciente' || (acao.multiplicador ?? 0) >= 1;
/** `doubledTasks` da página. */
export const acaoBonificada = (acao: AcaoEcoa) => (acao.multiplicador ?? 0) > 2;

const CRITERIOS: Record<CriterioDaFila, (acao: AcaoEcoa, contexto: ContextoDaFila) => boolean> = {
  aguardando: aguardandoValidacao,
  cota: (acao, { semestral }) => (semestral ? acaoBonificada(acao) : acaoValidada(acao)),
  ciencia: acaoComCiencia,
  'sem-feedback': (acao, { chaveDoUsuario }) => !temFeedbackDoUsuario(acao, chaveDoUsuario),
};

/** Teto das cotas de hoje: 1 validação por fase mensal (`validTasks.length/1`) e 1 bonificação (`maxDoubledTasks`). */
const COTA = 1;

/** ECN-49: os quatro indicadores do design, com os contadores de hoje. */
export function indicadoresDaFila(lista: AcaoEcoa[], contexto: ContextoDaFila): IndicadorDaFilaEcoa[] {
  const contar = (criterio: CriterioDaFila) => lista.filter(acao => CRITERIOS[criterio](acao, contexto)).length;
  const total = lista.length;

  return [
    { criterio: 'aguardando', rotulo: 'Aguardando sua validação', valor: String(contar('aguardando')) },
    {
      criterio: 'cota',
      rotulo: contexto.semestral ? 'Ações bonificadas' : 'Ações validadas',
      valor: `${contar('cota')}/${COTA}`,
    },
    { criterio: 'ciencia', rotulo: 'Encerradas com ciência', valor: `${contar('ciencia')}/${total}` },
    { criterio: 'sem-feedback', rotulo: 'Feedbacks concedidos', valor: `${total - contar('sem-feedback')}/${total}` },
  ];
}

/** Texto comparável da busca: sem acento e em minúsculas. */
function comparavel(texto: unknown): string {
  return String(texto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

function casaComABusca(acao: AcaoEcoa, termo: string): boolean {
  return [acao.title, acao.owner_name, acao.owner_name_guerra, acao.owner, acao.indicator?.nome].some(campo =>
    comparavel(campo).includes(termo),
  );
}

export interface FiltrosDaFilaEcoa {
  indicador: CriterioDaFila | null;
  busca: string;
}

/** ECN-50 e ECN-51: indicador ativo e "Buscar na lista", sem mudar a ordem recebida. */
export function filtrarFila<T extends AcaoEcoa>(
  lista: T[],
  { indicador, busca }: FiltrosDaFilaEcoa,
  contexto: ContextoDaFila,
): T[] {
  const termo = comparavel(busca.trim());

  return lista.filter(
    acao => (!indicador || CRITERIOS[indicador](acao, contexto)) && (!termo || casaComABusca(acao, termo)),
  );
}

/** ECN-52: opções de "Ordenar por"; "Ordem atual" (a do aggregate) é a padrão. */
export const OPCOES_DE_ORDEM_ECOA: { value: OrdemDaFila; label: string }[] = [
  { value: 'atual', label: 'Ordem atual' },
  { value: 'antigas', label: 'Cadastro mais antigo' },
  { value: 'recentes', label: 'Cadastro mais recente' },
  { value: 'nome', label: 'Nome da ação' },
];

const SEM_VALOR = '—';

const ROTULO_DO_STATUS: Record<string, string> = {
  aguardando: 'Aguardando validação',
  ciente: 'Encerrada com ciência',
  rejeitado: 'Devolvida',
  aprovado: 'Validada',
  dobrado: 'Bonificada',
  concluido: 'Finalizada',
  concluidoAuto: 'Finalizada',
};

/** Período da ação no fuso do `getFormattedDate` do cartão (GMT), com o ano inteiro. */
function dataDoPeriodo(valor: number): string {
  return format(utcToZonedTime(valor, 'GMT'), 'dd/MM/yyyy');
}

function dataLocal(valor: number | undefined): string {
  return typeof valor === 'number' ? format(new Date(valor), 'dd/MM/yyyy') : SEM_VALOR;
}

/** ECN-53: uma linha por ação da lista exibida, com as colunas da spec. */
export function linhasDaPlanilha(lista: AcaoEcoa[], contexto: ContextoDaFila) {
  return lista.map(acao => ({
    Ação: acao.title ?? '',
    'Cadastrada por': acao.owner_name ?? '',
    Chave: acao.owner ?? '',
    Eixo: eixoDaAcao(acao),
    Período: acao.start && acao.end ? `${dataDoPeriodo(acao.start)} a ${dataDoPeriodo(acao.end)}` : SEM_VALOR,
    Integrantes: Array.isArray(acao.team) ? acao.team.length : 0,
    Status: ROTULO_DO_STATUS[acao.status] ?? acao.status ?? SEM_VALOR,
    Pontos: typeof acao.multiplicador === 'number' ? acao.multiplicador * 100 : 0,
    Feedback: temFeedbackDoUsuario(acao, contexto.chaveDoUsuario) ? 'Concedido' : 'Sem feedback',
    Cadastro: dataLocal(acao.created),
    Atualização: dataLocal(acao.updated),
  }));
}
